import {
  PartnershipAcceptancePayload,
  PartnershipDetails,
  PartnershipListItem,
  PartnershipPayoutPercentagePayload,
} from '@/models/partnership.model'
import { RelatedService } from '@/models/public/service.model'
import axios from 'axios'
import { computed, ComputedRef, Ref } from 'vue'
import { useMutation, useQuery, useQueryClient } from 'vue-query'
import i18n from '../i18n'

export const partnershipKeys = {
  all: ['partnership'] as const,
  lists: () => [...partnershipKeys.all, 'list'] as const,
  list: (companyId: Ref<number | null>, ordering: Ref<string | null>) =>
    [...partnershipKeys.lists(), { companyId, ordering }] as const,
  details: () => [...partnershipKeys.all, 'detail'] as const,
  detail: (companyId: Ref<number | null>, partnershipId: Ref<number | null>) =>
    [...partnershipKeys.details(), { companyId, partnershipId }] as const,
  partnerServicesList: (
    serviceId: Ref<string | number | null>,
    locale: ComputedRef<string>,
  ) => [...partnershipKeys.lists(), { serviceId, locale }] as const,
}

export function usePartnershipListQuery(
  companyId: Ref<number | null>,
  ordering: Ref<string | null>,
) {
  return useQuery(
    partnershipKeys.list(companyId, ordering),
    async () => {
      const { data } = await axios.get<PartnershipListItem[]>(
        `companies/${companyId.value}/partner-companies/`,
        {
          params: {
            ordering: ordering.value,
          },
        },
      )
      return data
    },
    {
      placeholderData: () => [],
      enabled: computed(() => !!companyId.value),
      retry: 0,
    },
  )
}

export function usePartnershipDetailsMutation(
  companyId: Ref<number | null>,
  partnershipId: Ref<number | null>,
) {
  return useMutation(async () => {
    const { data } = await axios.get<PartnershipDetails>(
      `companies/${companyId.value}/partner-companies/${partnershipId.value}/`,
      {
        params: {},
      },
    )
    return data
  })
}

export function usePartnershipDetailsQuery(
  companyId: Ref<number | null>,
  partnershipId: Ref<number | null>,
) {
  return useQuery(
    partnershipKeys.detail(companyId, partnershipId),
    async () => {
      const { data } = await axios.get<PartnershipDetails>(
        `companies/${companyId.value}/partner-companies/${partnershipId.value}/`,
        {
          params: {},
        },
      )
      return data
    },
    {
      enabled: computed(() => !!companyId.value && !!partnershipId.value),
      retry: 0,
    },
  )
}

export function usePartnershipAcceptanceMutation(
  companyId: Ref<number | null>,
  partnershipId: Ref<number | null>,
) {
  const queryClient = useQueryClient()
  return useMutation(
    async (payload: PartnershipAcceptancePayload) => {
      const { data } = await axios.put<PartnershipDetails>(
        `companies/${companyId.value}/partner-companies/${partnershipId.value}/acceptance/`,
        payload,
      )
      return data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(partnershipKeys.lists())
        queryClient.invalidateQueries(
          partnershipKeys.detail(companyId, partnershipId),
        )
      },
    },
  )
}

export function usePartnershipPayoutPercentageMutation(
  companyId: Ref<number | null>,
  partnershipId: Ref<number | null>,
) {
  const queryClient = useQueryClient()
  return useMutation(
    async (payload: PartnershipPayoutPercentagePayload) => {
      const { data } = await axios.put<PartnershipDetails>(
        `companies/${companyId.value}/partner-companies/${partnershipId.value}/payout-percentage/`,
        payload,
      )
      return data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(partnershipKeys.lists())
        queryClient.invalidateQueries(
          partnershipKeys.detail(companyId, partnershipId),
        )
      },
    },
  )
}

export function usePublicServiceRelatedPartnerServicesQuery(
  serviceId: Ref<string | number | null>,
  isPreview: boolean,
) {
  const localeRef: any = i18n.global.locale
  return useQuery(
    partnershipKeys.partnerServicesList(serviceId, localeRef),
    async () => {
      // Preview endpoint is for service provider user usage in service preview
      // this query returns other housing type services provided by this company

      const companyHousingUnitsUrl = isPreview
        ? `public/service/${serviceId.value}/related-partner-services/preview/`
        : `public/service/${serviceId.value}/related-partner-services/`

      const { data } = await axios.get<RelatedService[]>(companyHousingUnitsUrl)
      return data
      //     path("public/service/<uuid:hash_id>/related-services/", views.PartnerCompaniesRelatedServicesView.as_view()),
    },
    {
      enabled: computed(() => !!serviceId.value),
      retry: 0,
    },
  )
}
